import { c as _c } from "react/compiler-runtime";
import { Background, BackgroundVariant, Controls, type EdgeTypes, type Node, type NodeTypes, ReactFlow, SelectionMode, useReactFlow } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { MonacoProvider, useEscOperationStack, useShortcut } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { GraphUndoRedo } from '@/features/playbook-edit';
import { ActionNode } from './components/action-node';
import AiDecisionNode from './components/ai-decision-node';
import AiResearchNode from './components/ai-research-node';
import AuthenticationNode from './components/authentication-node';
import BankLinkNode from './components/bank-link-node';
import BoKycNode from './components/bo-kyc-node';
import BranchNode from './components/branch-node';
import BusinessDataNode from './components/business-data-node';
import CodeExecNode from './components/code-exec-node';
import CollectCardNode from './components/collect-card-node';
import CollectDataNode from './components/collect-data-node';
import CollectDocumentNode from './components/collect-document-node';
import CompleteNode from './components/complete-node';
import ConditionalNode from './components/conditional-node';
import { CustomDataNode } from './components/custom-data-node';
import { CustomDocumentNode } from './components/custom-document';
import DataAssignmentNode from './components/data-assignment-node/data-assignment-node';
import DocumentExtractionNode from './components/document-extraction-node';
import Edge from './components/edge';
import { EdgeLabelNode } from './components/edge-label-node';
import ErrorWarningList from './components/error-warning-list';
import ExpressionNode from './components/expression-node';
import ExternalApiNode from './components/external-api-node';
import IdentityDataNode from './components/identity-data-node';
import { IdentityDocumentNode } from './components/identity-document';
import InputSchemaNode from './components/input-schema-node';
import InvestorProfileNode from './components/investor-profile-node';
import NodePanel from './components/node-panel/node-panel';
import { NodePanelProvider } from './components/node-panel/node-panel-context';
import OutputNode from './components/output-node';
import PlaceholderNode from './components/placeholder-node';
import PrerequisiteNode from './components/prerequisite-node';
import { RulesNode } from './components/rules-node';
import { SelfieNode } from './components/selfie-node';
import { TaktileNode } from './components/taktile-node';
import VendorIntegrationNode from './components/vendor-integration-node';
import VerificationCheckNode from './components/verification-check-node';
import { VerifiedPrefillNode } from './components/verified-prefill-node';
import WatchlistReviewNode from './components/watchlist-review-node';
import { useGetStatuses } from './hooks/use-get-statuses';
import useLayout from './hooks/use-layout';
import useMultiSelect from './hooks/use-multi-select';
import useSidebar from './hooks/use-sidebar';
import useStore from './hooks/use-store';
import { defaultEdgeOptions, HIGHLIGHTED_EDGE_CLASS_NAME, HIGHLIGHTED_EDGE_Z_INDEX } from './playbook-builder.constants';
import { type NodeTypesKey, reactFlowNodeType } from './playbook-builder.types';
const nodeComponents: Record<NodeTypesKey, NodeTypes[string]> = {
  action: ActionNode,
  ai_research: AiResearchNode,
  ai_decision: AiDecisionNode,
  authentication: AuthenticationNode,
  collect_data: CollectDataNode,
  collect_custom_data: CustomDataNode,
  collect_document: CollectDocumentNode,
  identity_document: IdentityDocumentNode,
  selfie: SelfieNode,
  custom_document: CustomDocumentNode,
  conditional: ConditionalNode,
  rules: RulesNode,
  verification_check: VerificationCheckNode,
  verified_prefill: VerifiedPrefillNode,
  bo_kyc: BoKycNode,
  investor_profile: InvestorProfileNode,
  taktile: TaktileNode,
  input_schema: InputSchemaNode,
  prerequisite: PrerequisiteNode,
  output: OutputNode,
  complete: CompleteNode,
  identity_data: IdentityDataNode,
  business_data: BusinessDataNode,
  external_api: ExternalApiNode,
  vendor_integration: VendorIntegrationNode,
  placeholder: PlaceholderNode,
  join_point: PlaceholderNode,
  branch: BranchNode,
  collect_card_data: CollectCardNode,
  bank_link: BankLinkNode,
  edge_label: EdgeLabelNode,
  assign_data: DataAssignmentNode,
  common_expression: ExpressionNode,
  code_exec: CodeExecNode,
  document_extraction: DocumentExtractionNode,
  watchlist_review: WatchlistReviewNode
};
const nodeTypes: NodeTypes = Object.fromEntries(Object.entries(nodeComponents).map(([kind, component]) => [reactFlowNodeType(kind as NodeTypesKey), component]));
const edgeTypes: EdgeTypes = {
  edge: Edge
};
type PlaybookBuilderProps = {
  bannerComponent?: React.ReactNode;
  headerComponent?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
};
const PlaybookBuilder = t0 => {
  const $ = _c(54);
  const {
    bannerComponent,
    headerComponent,
    className,
    children
  } = t0;
  const {
    nodes,
    edges,
    onNodesChange,
    onEdgesChange,
    isInitialized
  } = useLayout();
  const reactFlow = useReactFlow();
  const {
    data: t1,
    visibleBanners,
    executionTraceBanners
  } = useStore();
  const {
    canEdit
  } = t1;
  let t2;
  if ($[0] !== executionTraceBanners || $[1] !== visibleBanners) {
    t2 = [...visibleBanners, ...executionTraceBanners];
    $[0] = executionTraceBanners;
    $[1] = visibleBanners;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const errorItems = t2;
  useGetStatuses();
  const {
    isShiftPressed,
    onSelectionChange
  } = useMultiSelect();
  let t3;
  if ($[3] !== reactFlow) {
    t3 = (_, node) => {
      if (!node.selectable) {
        return;
      }
      reactFlow.setNodes(nodes_0 => nodes_0.map(n => n.id === node.id ? {
        ...n,
        selected: false
      } : n));
    };
    $[3] = reactFlow;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const undoNodeSelection = t3;
  const {
    onEscKeyDown
  } = useEscOperationStack();
  let t4;
  if ($[5] !== onEscKeyDown) {
    t4 = {
      onEscKeyDown
    };
    $[5] = onEscKeyDown;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  useShortcut(t4);
  const {
    isAnyOpen
  } = useSidebar();
  let t5;
  if ($[7] !== isAnyOpen || $[8] !== onEscKeyDown) {
    t5 = e => {
      if (isAnyOpen && e.target instanceof HTMLElement && e.target.classList.contains("draggable")) {
        onEscKeyDown();
      }
    };
    $[7] = isAnyOpen;
    $[8] = onEscKeyDown;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  const handleGraphClick = t5;
  let t6;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <style>{`
        .playbook-builder svg:has(path.${HIGHLIGHTED_EDGE_CLASS_NAME}) {
          z-index: ${HIGHLIGHTED_EDGE_Z_INDEX} !important;
        }
        .playbook-builder .react-flow__panel.bottom {
          bottom: 40px;
        }
        .playbook-builder .react-flow__selection {
          background: rgba(59, 130, 246, 0.06);
          border: 1px dashed rgba(59, 130, 246, 0.5);
          border-radius: 4px;
        }
        .playbook-builder .react-flow__node.selected > div {
          outline: 2px solid rgba(59, 130, 246, 0.6);
          outline-offset: 2px;
          border-radius: 10px;
        }
      `}</style>;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== className) {
    t7 = cx("flex relative flex-col flex-1 min-h-0 overflow-hidden playbook-builder", className);
    $[11] = className;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = !isInitialized;
  const t9 = !!children;
  let t10;
  if ($[13] !== t9) {
    t10 = cx("relative flex flex-1 min-h-0 bg-canvas", {
      hidden: t9
    });
    $[13] = t9;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = {
      x: 0,
      y: 0,
      zoom: 1.25
    };
    $[15] = t11;
  } else {
    t11 = $[15];
  }
  const t12 = !isInitialized;
  const t13 = !isShiftPressed;
  let t14;
  if ($[16] !== isShiftPressed || $[17] !== t12 || $[18] !== t13) {
    t14 = cx("flex-1 transition-opacity duration-200", {
      "opacity-0": t12,
      "cursor-crosshair": isShiftPressed,
      "cursor-grab": t13
    });
    $[16] = isShiftPressed;
    $[17] = t12;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  const t15 = !isShiftPressed;
  let t16;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = {
      hideAttribution: true
    };
    $[20] = t16;
  } else {
    t16 = $[20];
  }
  let t17;
  let t18;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = <Background color="var(--playbook-canvas-dot)" variant={BackgroundVariant.Dots} size={2} gap={20} />;
    t18 = <Controls showInteractive={false} className="!shadow-sm rounded-default overflow-hidden [&>button]:!bg-primary [&>button]:!text-primary [&>button]:!border-tertiary [&>button]:hover:!bg-hover [&>button]:hover:!text-tertiary fixed !bottom-3 max-md:bottom-[max(0.75rem,env(safe-area-inset-bottom))]!" />;
    $[21] = t17;
    $[22] = t18;
  } else {
    t17 = $[21];
    t18 = $[22];
  }
  let t19;
  if ($[23] !== children || $[24] !== errorItems) {
    t19 = children ? null : <ErrorWarningList items={errorItems} />;
    $[23] = children;
    $[24] = errorItems;
    $[25] = t19;
  } else {
    t19 = $[25];
  }
  let t20;
  if ($[26] !== canEdit) {
    t20 = canEdit && <GraphUndoRedo />;
    $[26] = canEdit;
    $[27] = t20;
  } else {
    t20 = $[27];
  }
  let t21;
  if ($[28] !== edges || $[29] !== handleGraphClick || $[30] !== isShiftPressed || $[31] !== nodes || $[32] !== onEdgesChange || $[33] !== onNodesChange || $[34] !== onSelectionChange || $[35] !== t14 || $[36] !== t15 || $[37] !== t19 || $[38] !== t20 || $[39] !== undoNodeSelection) {
    t21 = <ReactFlow defaultEdgeOptions={defaultEdgeOptions} defaultViewport={t11} edges={edges} edgeTypes={edgeTypes} nodes={nodes} nodesDraggable={false} nodeTypes={nodeTypes} onEdgesChange={onEdgesChange} onClick={handleGraphClick} className={t14} onNodesChange={onNodesChange} onSelectionChange={onSelectionChange} selectionOnDrag={isShiftPressed} selectionMode={SelectionMode.Partial} panOnDrag={t15} onNodeClick={undoNodeSelection} proOptions={t16} deleteKeyCode={null} maxZoom={1} minZoom={0.3}>{t17}{t18}{t19}{t20}</ReactFlow>;
    $[28] = edges;
    $[29] = handleGraphClick;
    $[30] = isShiftPressed;
    $[31] = nodes;
    $[32] = onEdgesChange;
    $[33] = onNodesChange;
    $[34] = onSelectionChange;
    $[35] = t14;
    $[36] = t15;
    $[37] = t19;
    $[38] = t20;
    $[39] = undoNodeSelection;
    $[40] = t21;
  } else {
    t21 = $[40];
  }
  let t22;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <NodePanel />;
    $[41] = t22;
  } else {
    t22 = $[41];
  }
  let t23;
  if ($[42] !== t21) {
    t23 = <MonacoProvider languageName="template">{t21}{t22}</MonacoProvider>;
    $[42] = t21;
    $[43] = t23;
  } else {
    t23 = $[43];
  }
  let t24;
  if ($[44] !== t10 || $[45] !== t23 || $[46] !== t8) {
    t24 = <div data-percy-context-container={true} aria-busy={t8} className={t10}>{t23}</div>;
    $[44] = t10;
    $[45] = t23;
    $[46] = t8;
    $[47] = t24;
  } else {
    t24 = $[47];
  }
  let t25;
  if ($[48] !== bannerComponent || $[49] !== children || $[50] !== headerComponent || $[51] !== t24 || $[52] !== t7) {
    t25 = <>{t6}<NodePanelProvider><div className={t7}>{bannerComponent}{headerComponent}{children}{t24}</div></NodePanelProvider></>;
    $[48] = bannerComponent;
    $[49] = children;
    $[50] = headerComponent;
    $[51] = t24;
    $[52] = t7;
    $[53] = t25;
  } else {
    t25 = $[53];
  }
  return t25;
};
export default PlaybookBuilder;